import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import { DashboardLayout } from "./components/layout/DashboardLayout";
import { authClient } from "./lib/auth-client";
import { requireAdmin, requireAuth, requireClaimsOps } from "./lib/route-guards";
import { RootLayout } from "./routes/__root";
import { AgencyDashboardPage } from "./routes/agency/dashboard";
import { ClaimsDashboardPage } from "./routes/claims/dashboard";
import { ClaimsMrpPage } from "./routes/claims/mrp";
import { ConfigAnnouncementsPage } from "./routes/config/announcements";
import { BrandingPage } from "./routes/config/branding";
import { CancelReasonsPage } from "./routes/config/cancel-reasons";
import { ConfigIndexPage, ConfigLayoutPage } from "./routes/config/index";
import { PayPlanPage } from "./routes/config/pay-plan";
import { RegionPage } from "./routes/config/region";
import { UploadPdfPage } from "./routes/config/upload-pdf";
import { ConfigUsersPage } from "./routes/config/users";
import { ConfigWorkflowsPage } from "./routes/config/workflows";
// ── Page imports ──
import { HomePage } from "./routes/home/index";
import { LoginPage } from "./routes/login";
import { PolicyDashboardPage } from "./routes/policy/dashboard";
import { PolicyDetailMrpPage } from "./routes/policy/detail-mrp";
import { PolicyLossRatioPage } from "./routes/policy/loss-ratio";
import { ClaimDepartmentListPage } from "./routes/teams/department";
import { ClaimTeamHelpPage } from "./routes/teams/help";
import { ClaimTeamHistoryPage } from "./routes/teams/history";
import { ClaimTeamMaintenancePage } from "./routes/teams/maintenance";
import { ClaimTeamsUnboundPage } from "./routes/teams/unbound";
import {
  claimsDashboardSearchSchema,
  claimsMrpSearchSchema,
  policyDashboardSearchSchema,
  policyMrpSearchSchema,
} from "./schemas/search";

// ── Root ──
const rootRoute = createRootRoute({ component: RootLayout });

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/home" });
  },
});

// ── Auth routes ──
// MEMIC C360 has no self-registration — all access is via Active Directory
// (AD OAuth lands in Phase 10). The login page remains as the bootstrap entry
// point for the seeded admin and any test users; sign-up is intentionally
// removed at the route, UI, and Better Auth API levels.
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  beforeLoad: async () => {
    // Already-authenticated users land back on /home instead of seeing the
    // login form again.
    const session = await authClient.getSession();
    if (session.data) throw redirect({ to: "/home" });
  },
  component: LoginPage,
});

// ── Authenticated layout ──
function AppLayoutRoute() {
  return (
    <DashboardLayout>
      <Outlet />
    </DashboardLayout>
  );
}

const appLayout = createRoute({
  getParentRoute: () => rootRoute,
  id: "app",
  beforeLoad: requireAuth,
  component: AppLayoutRoute,
});

// ── Home ──
const homeRoute = createRoute({
  getParentRoute: () => appLayout,
  path: "/home",
  component: HomePage,
});

// ── Policy routes ──
const policyDashboardRoute = createRoute({
  getParentRoute: () => appLayout,
  path: "/policy/$policyNumber",
  validateSearch: policyDashboardSearchSchema,
  component: PolicyDashboardPage,
});

const policyListRoute = createRoute({
  getParentRoute: () => appLayout,
  path: "/policy/list",
  validateSearch: policyMrpSearchSchema,
  component: PolicyDetailMrpPage,
});

const policyLossRatioRoute = createRoute({
  getParentRoute: () => appLayout,
  path: "/policy/$policyNumber/loss-ratio",
  component: PolicyLossRatioPage,
});

// ── Claims routes ──
const claimsDashboardRoute = createRoute({
  getParentRoute: () => appLayout,
  path: "/claims/$claimNumber",
  validateSearch: claimsDashboardSearchSchema,
  component: ClaimsDashboardPage,
});

const claimsMrpRoute = createRoute({
  getParentRoute: () => appLayout,
  path: "/claims/mrp",
  validateSearch: claimsMrpSearchSchema,
  component: ClaimsMrpPage,
});

// ── Agency routes ──
const agencyDashboardRoute = createRoute({
  getParentRoute: () => appLayout,
  path: "/agency/$agentNumber",
  component: AgencyDashboardPage,
});

// ── Claims operations (claims_ops + admin) ──
const teamsMaintenanceRoute = createRoute({
  getParentRoute: () => appLayout,
  path: "/teams",
  beforeLoad: requireClaimsOps,
  component: ClaimTeamMaintenancePage,
});

const teamsHistoryRoute = createRoute({
  getParentRoute: () => appLayout,
  path: "/teams/history",
  beforeLoad: requireClaimsOps,
  component: ClaimTeamHistoryPage,
});

const teamsDepartmentRoute = createRoute({
  getParentRoute: () => appLayout,
  path: "/teams/department",
  beforeLoad: requireClaimsOps,
  component: ClaimDepartmentListPage,
});

const teamsUnboundRoute = createRoute({
  getParentRoute: () => appLayout,
  path: "/teams/unbound",
  beforeLoad: requireClaimsOps,
  component: ClaimTeamsUnboundPage,
});

const teamsHelpRoute = createRoute({
  getParentRoute: () => appLayout,
  path: "/teams/help",
  beforeLoad: requireClaimsOps,
  component: ClaimTeamHelpPage,
});

// ── Admin/config routes ──
// Parent layout route: renders nav + <Outlet /> so sub-pages live inside.
const configLayoutRoute = createRoute({
  getParentRoute: () => appLayout,
  id: "configLayout",
  beforeLoad: requireAdmin,
  component: ConfigLayoutPage,
});

const configIndexRoute = createRoute({
  getParentRoute: () => configLayoutRoute,
  path: "/config",
  component: ConfigIndexPage,
});

const configAnnouncementsRoute = createRoute({
  getParentRoute: () => configLayoutRoute,
  path: "/config/announcements",
  component: ConfigAnnouncementsPage,
});

const configBrandingRoute = createRoute({
  getParentRoute: () => configLayoutRoute,
  path: "/config/branding",
  component: BrandingPage,
});

const configCancelReasonsRoute = createRoute({
  getParentRoute: () => configLayoutRoute,
  path: "/config/cancel-reasons",
  component: CancelReasonsPage,
});

const configPayPlanRoute = createRoute({
  getParentRoute: () => configLayoutRoute,
  path: "/config/pay-plan",
  component: PayPlanPage,
});

const configRegionRoute = createRoute({
  getParentRoute: () => configLayoutRoute,
  path: "/config/region",
  component: RegionPage,
});

const configUploadPdfRoute = createRoute({
  getParentRoute: () => configLayoutRoute,
  path: "/config/upload-pdf",
  component: UploadPdfPage,
});

const configUsersRoute = createRoute({
  getParentRoute: () => configLayoutRoute,
  path: "/config/users",
  component: ConfigUsersPage,
});

const configWorkflowsRoute = createRoute({
  getParentRoute: () => configLayoutRoute,
  path: "/config/workflows",
  component: ConfigWorkflowsPage,
});

// ── Route tree ──
const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  appLayout.addChildren([
    homeRoute,
    // Policy
    policyListRoute,
    policyDashboardRoute,
    policyLossRatioRoute,
    // Claims
    claimsMrpRoute,
    claimsDashboardRoute,
    // Agency
    agencyDashboardRoute,
    // Teams
    teamsHelpRoute,
    teamsMaintenanceRoute,
    teamsHistoryRoute,
    teamsDepartmentRoute,
    teamsUnboundRoute,
    // Config — parent layout route with children rendered into its Outlet
    configLayoutRoute.addChildren([
      configIndexRoute,
      configAnnouncementsRoute,
      configBrandingRoute,
      configCancelReasonsRoute,
      configPayPlanRoute,
      configRegionRoute,
      configUploadPdfRoute,
      configUsersRoute,
      configWorkflowsRoute,
    ]),
  ]),
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
